import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Airplane, LockKey, Envelope } from "@phosphor-icons/react";

const BG = "https://images.unsplash.com/photo-1542296332-2e4473faf563?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwyfHxhaXIlMjBjYXJnbyUyMGFpcnBsYW5lJTIwbG9naXN0aWNzfGVufDB8fHx8MTc4NDYwNTE4Mnww&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { user, ready, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (ready && user) {
    if (user.role === "punch_worker") return <Navigate to="/punch" replace />;
    if (user.role === "punch_admin") return <Navigate to="/punch-admin" replace />;
    return <Navigate to="/dashboard" replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      const u = res.error === undefined ? null : null; // placeholder
      // Redirect based on role — get from response via context (user is set)
      const stored = JSON.parse(localStorage.getItem("access_token") ? "true" : "false");
      // Simpler: refetch from /auth/me indirectly by checking user after set
      setTimeout(() => {
        const cur = window.__lastUser;
        if (cur?.role === "punch_worker") navigate("/punch", { replace: true });
        else if (cur?.role === "punch_admin") navigate("/punch-admin", { replace: true });
        else navigate("/dashboard", { replace: true });
      }, 50);
    }
    else setError(res.error);
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
      {/* Hero */}
      <div
        className="hidden lg:block relative bg-zinc-950"
        style={{ backgroundImage: `url(${BG})`, backgroundSize: "cover", backgroundPosition: "center" }}
      >
        <div className="absolute inset-0 bg-zinc-950/70" />
        <div className="relative h-full flex flex-col justify-between p-10 text-white">
          <div className="flex items-center gap-2">
            <Airplane size={32} weight="duotone" className="text-[#FF4F00]" />
            <span className="font-display font-black tracking-tight text-2xl">TOM <span className="text-[#FF4F00]">CARGO</span> DOCS</span>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-zinc-400 mb-4">Air Cargo Filing System</div>
            <h1 className="font-display font-black text-5xl leading-[0.95] tracking-tight">
              Every AWB.<br />Every customs form.<br />
              <span className="text-[#FF4F00]">One search away.</span>
            </h1>
            <p className="mt-6 text-zinc-300 max-w-md">
              Upload, tag and retrieve AWB forms, customs paperwork, invoices, permits and terminal
              charges in seconds. Built for freight agents that don't have time to lose.
            </p>
          </div>
          <div className="text-xs text-zinc-500 font-mono">
            v1.0 · Tom Cargo Docs sys · JWT
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="flex items-center justify-center px-6 py-10 bg-white">
        <div className="w-full max-w-sm">
          <div className="mb-8">
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-3">Restricted Access</div>
            <h2 className="font-display font-black text-3xl tracking-tight text-zinc-950">Sign in</h2>
            <p className="text-sm text-zinc-500 mt-2">Use your agent or worker credentials.</p>
          </div>

          <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
            <div>
              <label className="text-[11px] font-mono uppercase tracking-widest text-zinc-500 block mb-1.5">Email</label>
              <div className="relative">
                <Envelope size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                <input
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="login-email-input"
                  className="w-full pl-9 pr-3 py-2.5 text-sm border border-zinc-200 focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent"
                  placeholder="admin@cargo.com"
                />
              </div>
            </div>
            <div>
              <label className="text-[11px] font-mono uppercase tracking-widest text-zinc-500 block mb-1.5">Password</label>
              <div className="relative">
                <LockKey size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                <input
                  type="password"
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password-input"
                  className="w-full pl-9 pr-3 py-2.5 text-sm border border-zinc-200 focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent"
                  placeholder="••••••••"
                />
              </div>
            </div>

            {error && (
              <div data-testid="login-error" className="text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="w-full bg-[#002FA7] text-white text-sm font-medium py-2.5 hover:bg-[#00227A] disabled:opacity-60 transition-colors duration-150"
            >
              {loading ? "Signing in…" : "Sign In"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
